import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NgbDateFormatterService } from './ngb-date-formatter.service';

import { environment } from '@environments/environment';

import { SelectedProduct, Product } from '@model/product-detail.model';
import { QuoteData, GetProductPriceRequest, CreateQuoteRequest, Quotation } from '@model/quotation.model';
import { Period, NumberOfPerson, MasterData } from '@model/master-data.model';

import {
  MAX_SIZE_PERIOD_LIST,
  DEFAULT_MAX_PERIOD,
  PERIOD_INCREMENT,
  DEFAULT_MIN_PERIOD,
  DEFAULT_YEAR_PERIOD
} from '@constant/quotation.constant';

import { TravelType, ChannelType } from '@enum/quotation.enum';

@Injectable({
  providedIn: 'root'
})
export class QuotationService {
  constructor(private http: HttpClient, private dateService: NgbDateFormatterService) {}

  private quoteData$: BehaviorSubject<QuoteData> = new BehaviorSubject(null);
  private selectedProduct$: BehaviorSubject<SelectedProduct> = new BehaviorSubject(null);

  private createQuoteData$: BehaviorSubject<CreateQuoteRequest> = new BehaviorSubject(null);
  private createQuoteResult$: BehaviorSubject<Quotation[]> = new BehaviorSubject(null);

  getQuoteData(): Observable<QuoteData> {
    return this.quoteData$.asObservable().pipe(filter((quote: QuoteData) => quote !== null));
  }

  getSelectedProduct(): Observable<SelectedProduct> {
    return this.selectedProduct$.asObservable();
  }

  getCreateQuoteData(): Observable<CreateQuoteRequest> {
    return this.createQuoteData$.asObservable();
  }

  getCreateQuoteResult(): Observable<Quotation[]> {
    return this.createQuoteResult$.asObservable();
  }

  getQuotePeriod(): Period[] {
    return Array.from(Array(MAX_SIZE_PERIOD_LIST).keys()).map((index: number) => {
      let numDay: number = 0;
      const currentNumDay: number = index * PERIOD_INCREMENT + DEFAULT_MIN_PERIOD;

      if (currentNumDay === 720) {
        // not have 720 days period, have only 732 days period
        numDay = DEFAULT_MAX_PERIOD;
      } else if (currentNumDay === 360) {
        // not have 360 days period, have only 366 days period
        numDay = DEFAULT_YEAR_PERIOD;
      } else {
        numDay = currentNumDay;
      }

      return {
        code: `${numDay}`,
        descTh: `${numDay}`,
        descEn: `${numDay}`
      };
    });
  }

  getQuoteNumberOfPerson(min: number, max: number): NumberOfPerson[] {
    return Array.from(Array(max - min + 1).keys()).map((index: number) => ({
      code: (index + min).toString(),
      descTh: (index + min).toString(),
      descEn: (index + min).toString()
    }));
  }

  changeQuoteData(data: QuoteData): void {
    this.quoteData$.next(data);
  }

  changeSelectedProduct(data: SelectedProduct): void {
    this.selectedProduct$.next(data);
  }

  changeCreateQuoteResult(data: Quotation[]): void {
    this.createQuoteResult$.next(data);
  }

  getProductPrice(quoteData: QuoteData): Observable<Product> {
    const { insurancePlan, numPeople, directDiscount, fromDate, toDate, type, travelType, channelType } = quoteData;

    let directDiscountGroup: string = null;
    if (channelType === ChannelType.DIRECT && directDiscount) {
      directDiscountGroup = directDiscount.directDiscountGroup;
    }

    const url: string = `${environment.endpoint}/coverages`;
    const body: GetProductPriceRequest = {
      topLevelId: parseInt(insurancePlan.topLevelId, 10),
      numberInsurer: parseInt(numPeople.code, 10),
      termStartDate: this.dateService.ngbDateToString(fromDate),
      termEndDate: this.dateService.ngbDateToString(toDate),
      subProductId: insurancePlan ? insurancePlan.subProductId : null,
      directDiscountGroup,
      packageType: type ? type.code : null,
      isAnnualTrip: travelType === TravelType.ANNUAL_TRIP,
      destinationCountries: quoteData.to.map((item: MasterData) => item.code),
      durationPeriod: travelType === TravelType.ANNUAL_TRIP ? parseInt(quoteData.durationPeriod.code, 10) : quoteData.duration
    };

    return this.http.post<Product>(url, body);
  }

  createQuote(body: CreateQuoteRequest): Observable<Quotation[]> {
    const url: string = `${environment.endpoint}/eta/quotations`;
    this.createQuoteData$.next(body);

    return this.http.post<Quotation[]>(url, body);
  }
}
